import React, { useState, useEffect, useRef, useCallback } from 'react';
import Joyride from 'react-joyride';

const ONBOARDING_VERSION = '1.1.0';

const Tour = ({ 
  currentUser, 
  currentUserLoading, 
  activeTab, 
  setActiveTab,
  onTourStart,
  onTourEnd,
  onTourRef
}) => {
  const [runTour, setRunTour] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const tourInitRef = useRef(false);

  const tourSteps = [
    {
      target: '[data-tour="app-root"]',
      placement: 'center',
      title: 'Sorta Friends',
      content: 'A social game where you rank anything from best to worst. Favorite book? Worst song? See how your answers stack up to others.',
      disableBeacon: true,
    },
    {
      target: '[data-tour="users-tab"]',
      title: 'Add Friends',
      content: 'Find others in the Users tab, and add them as friends.',
    },
    {
      target: '[data-tour="groups-tab"]',
      title: 'Join Groups',
      content: 'Join groups to get new themed stacks. Or create your own and invite others.',
    },
    {
      target: '[data-tour="stacks-tab"]',
      title: 'Access Stacks',
      content: 'Once you have friends or groups, create or answer stacks in the Stacks tab.',
    },
    {
      target: '[data-tour="notifications-envelope"]',
      title: 'Stay Connected',
      content: 'New friend or group requests will show up here. Look for the red dot to show you have a new message.',
    },
    {
      target: '[data-tour="settings-button"]',
      title: 'Adjust Your Profile',
      content: 'Update your bio, avatar, and Telegram or Signal in Settings.',
    },
  ];

  useEffect(() => {
    if (tourInitRef.current) return;
    // Start the tour only after user data is loaded so all targets exist
    if (currentUserLoading || !currentUser) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const storedVersion = localStorage.getItem('onboarding_version');
    const storedProgress = localStorage.getItem('onboarding_progress');
    const storedCompleted = localStorage.getItem('onboarding_completed') === 'true';

    if (storedVersion !== ONBOARDING_VERSION) {
      // Version changed: reset any completion and progress and start fresh
      localStorage.setItem('onboarding_version', ONBOARDING_VERSION);
      localStorage.removeItem('onboarding_progress');
      localStorage.removeItem('onboarding_completed');
      if (!reduce) {
        setRunTour(true);
        setTourStepIndex(0);
        tourInitRef.current = true;
        onTourStart?.();
      }
    } else if (!reduce && !storedCompleted && storedProgress) {
      // Resume only if a tour was in progress and not completed
      const index = parseInt(storedProgress, 10);
      if (!Number.isNaN(index) && index < tourSteps.length) {
        setTourStepIndex(index);
        setRunTour(true);
        tourInitRef.current = true;
        onTourStart?.();
      }
    }
  }, [currentUserLoading, currentUser, tourSteps.length, onTourStart]);

  const handleJoyrideCallback = (data) => {
    const { action, index, status, type } = data;
    const finishedStatuses = ['finished', 'skipped'];

    if (type === 'step:before') {
      // Ensure specific targets are visible for steps that depend on tab views
      if (index === 1) setActiveTab('users'); // Users tab
      if (index === 2) setActiveTab('groups'); // Groups tab
      if (index === 3) setActiveTab('stacks'); // Stacks tab
      if (index === 5 || index === 6 || index === 7) setActiveTab('settings'); // Bio, Avatar, and Social Settings live in Settings
    }

    if (type === 'step:after') {
      const nextIndex = (action === 'prev') ? Math.max(0, index - 1) : index + 1;

      // If the user clicks Next on the Settings step, programmatically navigate to Settings
      if (action === 'next' && index === 4) {
        setActiveTab('settings');
        // Delay advancing to allow Settings DOM to mount targets for the next step
        setTimeout(() => {
          localStorage.setItem('onboarding_progress', String(nextIndex));
          setTourStepIndex(nextIndex);
        }, 200);
        return;
      }

      localStorage.setItem('onboarding_progress', String(nextIndex));
      setTourStepIndex(nextIndex);
    }

    if (type === 'tour:end' || finishedStatuses.includes(status)) {
      setRunTour(false);
      localStorage.removeItem('onboarding_progress');
      localStorage.setItem('onboarding_completed', 'true');
      onTourEnd?.();
    }
  };

  // Function to start the tour manually (for "Show tour again" button)
  const startTour = useCallback(() => {
    localStorage.removeItem('onboarding_progress');
    localStorage.setItem('onboarding_version', ONBOARDING_VERSION);
    setTourStepIndex(0);
    setRunTour(true);
    onTourStart?.();
  }, [onTourStart]);

  // Expose startTour function to parent component
  useEffect(() => {
    if (onTourRef) {
      onTourRef({ startTour });
    }
  }, [onTourRef, startTour]);

  return (
    <Joyride
      run={runTour}
      stepIndex={tourStepIndex}
      steps={tourSteps}
      callback={handleJoyrideCallback}
      continuous
      showSkipButton
      showProgress
      disableScrolling
      spotlightClicks
      disableCloseOnEsc
      disableOverlayClose
      scrollToFirstStep
      styles={{
        options: {
          zIndex: 20000,
          backgroundColor: 'var(--color-surface)',
          textColor: 'var(--color-text)',
          primaryColor: 'var(--color-success)',
          arrowColor: 'var(--color-surface)',
          overlayColor: 'var(--overlay)'
        },
        tooltipContainer: {
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-text)'
        },
        tooltip: {
          backgroundColor: 'var(--color-surface)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: '8px',
          boxShadow: '0 4px 6px var(--color-shadow)'
        },
        tooltipTitle: {
          color: 'var(--color-text)'
        },
        tooltipContent: {
          color: 'var(--color-text)'
        },
        tooltipFooter: {
          borderTop: '1px solid var(--color-border)'
        },
        buttonNext: {
          backgroundColor: 'var(--color-success)',
          color: 'var(--text-on-success)',
          border: 'none'
        },
        buttonBack: {
          color: 'var(--color-text)'
        },
        buttonSkip: {
          backgroundColor: 'var(--color-border)',
          color: 'var(--color-text)'
        }
      }}
      locale={{ next: 'Next →', back: '← Back', last: 'Done', skip: 'Skip' }}
    />
  );
};

export default Tour;
